import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { LayoutGrafik, LayoutGrafikAnlegen, LayoutGrafikFelder } from "@kundeninfo/api-client";
import { Modal, ModalFuss, useZiehOrdnung } from "@kundeninfo/ui";
import type { ZiehOrdnung } from "@kundeninfo/ui";
import type { PanelProps } from "../LayoutRegister.js";
import { BILD_ACCEPT, istBildDateiname } from "../../../bildAuswahl.js";

/**
 * Register „Marke" (Spec, Oberfläche, rechts). Akzentfarbe und Logo gehören dem
 * UNTERNEHMEN und stehen hier nur zur Ansicht, mit Sprung dorthin. Die Grafiken sind
 * eigene Einträge am Layout: Jede Änderung ist ein Serverweg (`wege`). Der Entwurf bleibt
 * davon unberührt. Bearbeitet wird INLINE im Register (Vollseiten-Ausnahme, Entscheid 10);
 * Farben, Schrift und Logo sind in Etappe 1 nur zur Ansicht.
 */

export type GrafikTeil = Partial<Omit<LayoutGrafikFelder, "version">>;

/** Ein Rasterbild (PNG/JPEG) trägt im PDF keine Deckkraft (Nachtrag L5). */
function istRasterbild(g: LayoutGrafik): boolean {
  return g.bildTyp === "image/png" || g.bildTyp === "image/jpeg";
}

export type GrafikWege = {
  grafikUrl: (gid: string) => string;
  arbeitet: boolean;
  /** Befunde einer Ablehnung: an der Grafik `gid`; `"neu"` (Hochladen, Unterschriftsplatz)
   *  zeigt das Panel mit der Servermeldung unter „Datei hochladen" (Fixrunde 2, W1). */
  fehler: { gid: string; felder: Record<string, string>; meldung?: string } | null;
  ziehen: { gid: string; xMm: number; yMm: number } | null;
  onHochladen: (datei: File) => void;
  onUnterschriftsplatz: () => void;
  onAendern: (g: LayoutGrafik, teil: GrafikTeil) => void;
  onEntfernen: (g: LayoutGrafik) => void;
  onVerschieben: (von: number, nach: number) => void;
  onUnternehmen: () => void;
};

/** Vorgaben einer hochgeladenen Grafik: erste Seite, unten rechts im Satzspiegel. */
export const STEMPEL_VORGABE: Omit<LayoutGrafikAnlegen, "version" | "kind" | "name"> = {
  pages: "FIRST", xMm: 150, yMm: 240, widthMm: 40, opacity: 1, caption: false,
  documentKinds: ["ANGEBOT", "AUFTRAGSBESTAETIGUNG"],
};

/** Vorgaben des Unterschriftsplatzes: Angebot und Auftragsbestätigung, Name darunter. */
export const UNTERSCHRIFT_VORGABE: Omit<LayoutGrafikAnlegen, "version" | "kind"> = {
  name: "Unterschrift Vertreter", pages: "FIRST", xMm: 120, yMm: 250, widthMm: 50,
  opacity: 1, caption: true, documentKinds: ["ANGEBOT", "AUFTRAGSBESTAETIGUNG"],
};

const SEITEN_TEXT: Record<string, string> = {
  FIRST: "erste Seite", FOLLOWING: "Folgeseiten", ALL: "alle Seiten",
};

function zahl(text: string): number | null {
  const wert = Number(text.trim().replace(",", "."));
  return text.trim() === "" || !Number.isFinite(wert) ? null : wert;
}

type ZeileProps = {
  g: LayoutGrafik; index: number; anzahl: number; stand: PanelProps["stand"];
  wege: GrafikWege; ordnung: ZiehOrdnung; onEntfernenFragen: (g: LayoutGrafik) => void;
};

function GrafikZeile({ g, index, anzahl, stand, wege, ordnung, onEntfernenFragen }: ZeileProps):
    ReactElement {
  const zug = wege.ziehen?.gid === g.id ? wege.ziehen : null;
  const [name, setName] = useState(g.name);
  const [x, setX] = useState(String(g.xMm));
  const [y, setY] = useState(String(g.yMm));
  const [breite, setBreite] = useState(String(g.widthMm));
  const [deckkraft, setDeckkraft] = useState(String(Math.round(g.opacity * 100)));
  // Der Serverstand gewinnt, sobald er sich ändert (Antwort eines Wegs, Verwerfen).
  useEffect(() => {
    setName(g.name); setX(String(g.xMm)); setY(String(g.yMm));
    setBreite(String(g.widthMm)); setDeckkraft(String(Math.round(g.opacity * 100)));
  }, [g.name, g.xMm, g.yMm, g.widthMm, g.opacity]);

  const fehler = wege.fehler?.gid === g.id ? wege.fehler.felder : {};
  const fehlerId = (feld: string): string => `lay-grafik-${g.id}-${feld}-fehler`;
  const beschrieben = (feld: string): Record<string, string> =>
    fehler[feld] === undefined ? {} : { "aria-describedby": fehlerId(feld) };
  // Während eines Wegs sind Eingaben `readOnly` + `aria-disabled`, nicht `disabled`: Ein
  // gesperrtes Feld verlöre den Tastaturfokus an `body` (Fixrunde 2, W2). Auswahl und
  // Schalter verwerfen Änderungen; ein Verlassen-Weg geht in die Laufsperre des Editors.
  // Ein nach einer Ablehnung stehender Wert springt beim nächsten Serverstand derselben
  // Grafik auf diesen zurück (Effekt oben) — gewollt, der Serverstand gewinnt.
  const gesperrt = wege.arbeitet;

  /** Eine Zahl beim Verlassen: nur Gültiges und Geändertes geht hinaus; Ungültiges
   *  springt auf den Serverstand zurück. */
  function zahlVerlassen(text: string, alt: number, zuruecksetzen: () => void,
                         teil: (n: number) => GrafikTeil): void {
    const n = zahl(text);
    if (n === null) { zuruecksetzen(); return; }
    if (n !== alt) wege.onAendern(g, teil(n));
  }

  function mmFeld(feld: "xMm" | "yMm" | "widthMm", label: string, text: string,
                  setzen: (t: string) => void): ReactElement {
    const id = `lay-grafik-${g.id}-${feld}`;
    const wert = zug !== null && (feld === "xMm" || feld === "yMm") ? String(zug[feld]) : text;
    return (
      <div className="lay__feld lay__feld--klein">
        <label className="lay__label" htmlFor={id}>{label}</label>
        <div className="lay__eingabezeile">
          <input id={id} type="text" inputMode="decimal" className="lay__eingabe lay__eingabe--schmal"
            aria-label={`${g.name}: ${label}`} value={wert} readOnly={gesperrt}
            aria-disabled={gesperrt}
            aria-invalid={fehler[feld] !== undefined} {...beschrieben(feld)}
            onChange={(e) => setzen(e.target.value)}
            onBlur={() => zahlVerlassen(text, g[feld], () => setzen(String(g[feld])),
              (n) => (feld === "xMm" ? { xMm: n } : feld === "yMm" ? { yMm: n }
                : { widthMm: n }))} />
          <span className="lay__einheit">mm</span>
        </div>
      </div>
    );
  }

  return (
    <li className="lay__grafik" ref={ordnung.merke(index)} {...ordnung.zeile(index)}>
      <div className="lay__grafikkopf">
        <span className="lay__griff" {...ordnung.griff(index)}>⠿</span>
        {g.kind === "STATIC"
          ? <img className="lay__daumen" src={wege.grafikUrl(g.id)} alt="" />
          : <span className="lay__daumen lay__daumen--unterschrift" aria-hidden="true">✍</span>}
        <input type="text" className="lay__eingabe lay__grafikname" aria-label={`${g.name}: Name`}
          value={name} readOnly={gesperrt} aria-disabled={gesperrt}
          aria-invalid={fehler["name"] !== undefined}
          {...beschrieben("name")}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { if (name.trim() !== g.name) wege.onAendern(g, { name: name.trim() }); }} />
        <button type="button" className="lay__pfeil" aria-label={`${g.name} nach oben`}
          disabled={gesperrt || index === 0}
          onClick={() => wege.onVerschieben(index, index - 1)}>▲</button>
        <button type="button" className="lay__pfeil" aria-label={`${g.name} nach unten`}
          disabled={gesperrt || index === anzahl - 1}
          onClick={() => wege.onVerschieben(index, index + 1)}>▼</button>
        <button type="button" className="lay__knopf lay__knopf--leise"
          aria-label={`${g.name} entfernen`} disabled={gesperrt}
          onClick={() => onEntfernenFragen(g)}>Entfernen</button>
      </div>
      {g.kind === "SIGNATURE" && (
        <p className="lay__panelhinweis">Unterschrift des Vertreters am Beleg — im
          Muster: Beispielunterschrift.</p>
      )}
      <div className="lay__grafikfelder">
        <div className="lay__feld lay__feld--klein">
          <label className="lay__label" htmlFor={`lay-grafik-${g.id}-pages`}>Seiten</label>
          <select id={`lay-grafik-${g.id}-pages`} aria-label={`${g.name}: Seiten`}
            value={g.pages} aria-disabled={gesperrt}
            onChange={(e) => { if (!gesperrt) wege.onAendern(g, { pages: e.target.value }); }}>
            {stand.katalog.grafikSeiten.map((s) =>
              <option key={s} value={s}>{SEITEN_TEXT[s] ?? s}</option>)}
          </select>
        </div>
        {mmFeld("xMm", "x", x, setX)}
        {mmFeld("yMm", "y", y, setY)}
        {mmFeld("widthMm", "Breite", breite, setBreite)}
        {istRasterbild(g) ? (
          <div className="lay__feld lay__feld--klein">
            <span className="lay__label">Deckkraft</span>
            <span className="lay__panelhinweis">Deckkraft nur bei SVG</span>
          </div>
        ) : (
        <div className="lay__feld lay__feld--klein">
          <label className="lay__label" htmlFor={`lay-grafik-${g.id}-opacity`}>Deckkraft</label>
          <div className="lay__eingabezeile">
            <input id={`lay-grafik-${g.id}-opacity`} type="text" inputMode="numeric"
              className="lay__eingabe lay__eingabe--schmal" aria-label={`${g.name}: Deckkraft`}
              value={deckkraft} readOnly={gesperrt} aria-disabled={gesperrt}
              aria-invalid={fehler["opacity"] !== undefined} {...beschrieben("opacity")}
              onChange={(e) => setDeckkraft(e.target.value)}
              onBlur={() => zahlVerlassen(deckkraft, Math.round(g.opacity * 100),
                () => setDeckkraft(String(Math.round(g.opacity * 100))),
                (n) => ({ opacity: n / 100 }))} />
            <span className="lay__einheit">%</span>
          </div>
        </div>
        )}
      </div>
      {g.kind === "SIGNATURE" && (
        <div className="lay__grafikfelder">
          <label className="lay__werkzeug">
            <input type="checkbox" checked={g.caption} aria-disabled={gesperrt}
              aria-label={`${g.name}: Name darunter`}
              onChange={(e) => { if (!gesperrt) wege.onAendern(g, { caption: e.target.checked }); }} />
            Name darunter
          </label>
          {stand.katalog.belegarten.map((b) => (
            <label key={b.art} className="lay__werkzeug">
              <input type="checkbox" checked={g.documentKinds.includes(b.art)}
                aria-disabled={gesperrt} aria-label={`${g.name}: ${b.label}`}
                onChange={(e) => {
                  if (gesperrt) return;
                  const gewaehlt = new Set(g.documentKinds);
                  if (e.target.checked) gewaehlt.add(b.art); else gewaehlt.delete(b.art);
                  // In Katalogreihenfolge — so vergleicht sich der Stand stabil (R-VERGLEICH).
                  wege.onAendern(g, { documentKinds: stand.katalog.belegarten
                    .map((a) => a.art).filter((a) => gewaehlt.has(a)) });
                }} />
              {b.label}
            </label>
          ))}
        </div>
      )}
      {Object.entries(fehler).map(([feld, text]) => (
        <span key={feld} id={fehlerId(feld)} className="lay__feldfehler" role="alert">{text}</span>
      ))}
    </li>
  );
}

export function MarkePanel(p: PanelProps & { wege: GrafikWege }): ReactElement {
  const { stand, wege } = p;
  const [entfernen, setEntfernen] = useState<LayoutGrafik | null>(null);
  const [dateiFehler, setDateiFehler] = useState<string | null>(null);
  const grafiken = stand.grafiken;
  const ordnung = useZiehOrdnung(grafiken.length, (von, nach) => {
    if (!wege.arbeitet) wege.onVerschieben(von, nach);
  });
  const voll = grafiken.length >= 10;
  const hatUnterschrift = grafiken.some((g) => g.kind === "SIGNATURE");

  return (
    <div className="lay__panel">
      <fieldset className="lay__gruppe">
        <legend>Aus dem Unternehmen</legend>
        <div className="lay__eingabezeile">
          <span className="lay__farbfeld" style={{ background: stand.unternehmen.colorHex }}
            aria-hidden="true" />
          <span>Akzentfarbe des Unternehmens (aus den Stammdaten):{" "}
            <code>{stand.unternehmen.colorHex}</code></span>
        </div>
        <p>{stand.unternehmen.logoVorhanden ? "Logo hinterlegt"
          : "Kein Logo hinterlegt — der Beleg zeigt den Namen des Unternehmens."}</p>
        <button type="button" className="lay__knopf" onClick={wege.onUnternehmen}>
          Im Unternehmen pflegen</button>
      </fieldset>

      <fieldset className="lay__gruppe">
        <legend>Grafiken ({grafiken.length} von 10)</legend>
        {dateiFehler !== null && (
          <p className="lay__meldung lay__meldung--schlecht" role="alert">{dateiFehler}</p>
        )}
        <div className="lay__werkzeuge">
          <label className={`lay__knopf${voll || wege.arbeitet ? " lay__knopf--aus" : ""}`}>
            Datei hochladen
            <input type="file" className="lay__sr" accept={BILD_ACCEPT}
              aria-label="Datei hochladen (SVG, PNG oder JPEG)"
              disabled={voll || wege.arbeitet}
              onChange={(e) => {
                const datei = e.target.files?.[0];
                e.target.value = "";
                if (datei === undefined) return;
                // Nur .svg/.png/.jpg/.jpeg — den Inhalt prüft der Server an den Bytes
                // (GRAFIK_UNGUELTIG, GRAFIK_ZU_GROSS, Entscheid 25).
                if (!istBildDateiname(datei.name)) {
                  setDateiFehler("Nur SVG, PNG oder JPEG (.svg, .png, .jpg) sind zulässig.");
                  return;
                }
                setDateiFehler(null);
                wege.onHochladen(datei);
              }} />
          </label>
          <button type="button" className="lay__knopf"
            disabled={voll || hatUnterschrift || wege.arbeitet}
            onClick={wege.onUnterschriftsplatz}>Unterschriftsplatz hinzufügen</button>
        </div>
        {wege.fehler?.gid === "neu" && (
          <div className="lay__neufehler">
            {wege.fehler.meldung !== undefined && (
              <p className="lay__meldung lay__meldung--schlecht" role="alert">
                {wege.fehler.meldung}</p>
            )}
            {Object.keys(wege.fehler.felder).length > 0 && (
              <ul className="lay__feldfehler">
                {Object.entries(wege.fehler.felder).map(([feld, text]) =>
                  <li key={feld}>{text}</li>)}
              </ul>
            )}
          </div>
        )}
        <p className="lay__panelhinweis">Lage in Millimetern ab der linken oberen Blattkante;
          im Blatt lassen sich die Rahmen ziehen. Grafiken liegen unter dem Belegtext. SVG,
          PNG oder JPEG (höchstens 1 MB und 2000 × 2000 Pixel); die Deckkraft wirkt nur bei
          SVG.</p>
        {grafiken.length === 0
          ? <p className="lay__hinweis">Noch keine Grafiken.</p>
          : (
            <ol className="lay__grafiken">
              {grafiken.map((g, i) => (
                <GrafikZeile key={g.id} g={g} index={i} anzahl={grafiken.length} stand={stand}
                  wege={wege} ordnung={ordnung} onEntfernenFragen={setEntfernen} />
              ))}
            </ol>
          )}
      </fieldset>

      {entfernen !== null && (
        <Modal titel="Grafik entfernen" onAbbrechen={() => setEntfernen(null)}>
          <p>„{entfernen.name}“ wird aus dem Entwurf entfernt. Belege ändern sich erst mit
            der nächsten Freigabe.</p>
          <ModalFuss speichernText="Entfernen" onAbbrechen={() => setEntfernen(null)}
            onSpeichern={() => { wege.onEntfernen(entfernen); setEntfernen(null); }} />
        </Modal>
      )}
    </div>
  );
}
